import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {NEVER, Observable} from 'rxjs';
import {BasedataService} from '../shared/basedata.service';
import {FaultData, FaultsData, FaultSearch} from './fault-data';
import {ConfigService} from '@docma-mm-core/config/config.service';
import {catchError, map, takeUntil} from 'rxjs/operators';
import {Fault} from './fault';
import {FaultsEditData} from '@docma-mm-core/fault/faults-edit';
import {ToId} from '@docma-mm-core/api.functions';
import {FaultExportParams} from '@docma-mm-core/fault/fault-export-params';
import {Project} from '@docma-mm-core/project/project';

@Injectable()
export class FaultApiService extends BasedataService {

  private faultUrl = '/faults';

  constructor(private http: HttpClient, configService: ConfigService) {
    super(configService);
  }

  private getFaultUrl() {
    return super.getFullUrl(this.faultUrl);
  }

  // search faults
  searchFaults(structureId: number = null,
               poolId: number = null,
               top: number = null,
               skip: number = 0,
               companyId: number = null,
               statusId: number = null,
               layoutId: number = null,
               fields: string[] = [],
               faultIds: number[] = [],
               faultSUids: string[] = [],
               faultUniqueIds: string[] = [],
               additionalInformations: number = 11,
               cancelation?: Observable<any> ): Observable<FaultsData> {

    const endpoint = `${this.getFaultUrl()}/search`;

    const search = new FaultSearch({
      Fields: (fields && fields.length !== 0 ? fields : null),
      Structure: (structureId ? structureId.toString() : null),
      PoolId: (poolId ? poolId.toString() : null),
      LayoutId: (layoutId ? layoutId.toString() : null),
      Ids: (faultIds && faultIds.length !== 0 ? faultIds : null),
      SUids: (faultSUids && faultSUids.length !== 0 ? faultSUids : null),
      UniqueIds: (faultUniqueIds && faultUniqueIds.length !== 0 ? faultUniqueIds : null),
      Company: (companyId ? companyId.toString() : null),
      Status: (statusId ? statusId?.toString() : null),

      // TODO IN NEXT SPRINT
      AdditionalInformations: additionalInformations
    });

    let httpParams = new HttpParams();
    if (top) {
      httpParams = httpParams.set('$top', top.toString());
    }
    if (skip) {
      httpParams = httpParams.set('$skip', skip.toString());
    }
    httpParams = httpParams.set('overrideActivityVisibilityForAttachments', true);

    return this.http
      .post<FaultsData>(endpoint, search, { headers: this.getHeaders(), params: httpParams })
      .pipe(
        takeUntil(cancelation || NEVER),
        // tap(t => {
        //   console.warn('fault-api.service.searchFaults' + t);
        //   console.warn(t);
        // }),
        map(
        (data: FaultsData) => {
          if (data.Data) {
            data.Data = data.Data.map(fd => new FaultData(fd));
          }
          return data;
        }),
        catchError((e) => this.handleError(e))
      );
  }

  // // get fault by id
  // getFaultById(faultId: number): Observable<FaultData> {
  //   const endpoint = this.getFaultUrl() + '/' + faultId;
  //   return this.http
  //     .get<FaultData>(endpoint, { headers: this.getHeaders() })
  //     .pipe(
  //       map(f => f && new FaultData(f) ),
  //       catchError((e) => this.handleError(e))
  //     );
  // }

  // create full fault
  createFault(fault: FaultData): Observable<FaultData> {
    const endpoint = this.getFaultUrl() + '?autoActivities=true';

    let httpParams = new HttpParams();
    httpParams = httpParams.set('extendedErrors', 'true');

    return this.http
      .post<FaultData>(endpoint, JSON.stringify(fault), { headers: this.getHeaders(), params: httpParams })
      .pipe(
        map(f => f && new FaultData(f) ),
        catchError((e) => this.handleError(e))
      );

  }

  patchFaults(project: {mydocmaUid: string, projectId: number}, faultsEditData: FaultsEditData): Observable<void> {
    const endpoint = this.getFaultUrl() + '/patch?projectId=' + project.projectId;

    let httpParams = new HttpParams();
    httpParams = httpParams.set('extendedErrors', 'true');

    return this.http
      .post<void>(endpoint, JSON.stringify(faultsEditData), { headers: this.getHeaders(), params: httpParams })
      .pipe(catchError((e) => this.handleError(e))
      );
  }

  // update existing fault
  updateFault(faultId: any, fault: FaultData): Observable<FaultData> {
    const endpoint = this.getFaultUrl() + '/' + ToId(faultId);
    let httpParams = new HttpParams();
    httpParams = httpParams.set('extendedErrors', 'true');

    if (fault.PlanPositionX === null) {
      httpParams = httpParams.set('allowPinDelete', 'true');
    }
    return this.http
      .put<FaultData>(endpoint, JSON.stringify(fault), { headers: this.getHeaders(), params: httpParams })
      .pipe(
        map(f => f && new FaultData(f) ),
        catchError((e) => this.handleError(e))
      );
  }

  deleteFaults(faults: Fault[], bpConnected: boolean): Observable<any> {
    const endpoint = this.getFaultUrl();
    let httpParams = new HttpParams();
    httpParams = httpParams.set('isBimProject', bpConnected.toString());

    return this.http.delete(endpoint, { headers: this.getHeaders(), params: httpParams, body: { UniqueIds: faults.map(fault => fault.uid) } });
  }

  export(params: FaultExportParams, project: Project): Observable<{taskId: string, projectId: string}> {
    const endpoint = `${this.getFaultUrl()}/export`;

    const httpParams = new HttpParams();

    return this.http
      .post<string>(endpoint, params, { headers: this.getHeaders(), params: httpParams })
      .pipe(
        map(
          (taskId: string) => {
            return ({taskId, projectId: project.MyDocmaUid});
          })
          , catchError((e) => this.handleError(e))
      );
  }

}
